import { useState, type FormEvent } from 'react';
import {
  useApiKeys,
  useIssueApiKey,
  useMyApiKey,
  usePermissionCatalogue,
  useRevokeApiKey,
} from '../api/hooks';
import type { ApiKey, IssuedApiKey } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { EmptyState, ErrorBanner, Modal, formatDateTime, relativeTime } from '../components/ui';

/**
 * API credentials.
 *
 * A key here is not a separate account: it authenticates *as the person who
 * issued it*, with their permissions narrowed to what the key was given. The
 * page is written to keep that visible, because it is the thing that decides
 * what a leaked key can do — hence the owner column, the permission list on
 * every key, and the wording on the issue form.
 */

/** Mirrors the server's refusal list, so the UI never offers a doomed choice. */
const NEVER_ON_A_KEY = new Set([
  'user.manage',
  'role.manage',
  'lead.delete',
  'lead.merge',
  'api_key.manage',
]);

const DEFAULT_PERMISSIONS = ['lead.create'];

function CopyField({ label, value, mono = true }: { label: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="field">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly value={value} className={mono ? 'mono' : undefined} onFocus={(e) => e.currentTarget.select()} />
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/**
 * The one moment the secret exists outside a hash.
 *
 * Deliberately a modal that must be dismissed, with the warning above the
 * value rather than below it: a secret shown inline on a page is a secret
 * someone scrolls past and then cannot recover.
 */
function SecretModal({ issued, onClose }: { issued: IssuedApiKey; onClose: () => void }) {
  const [acknowledged, setAcknowledged] = useState(false);

  const example = `curl -X POST ${window.location.origin}/api/v1/leads \\
  -H "x-api-key: ${issued.key_id}" \\
  -H "x-api-secret: ${issued.api_secret}" \\
  -H "content-type: application/json" \\
  -d '{"full_name":"Ananya Rao","phone":"9812345678","source":"api"}'`;

  return (
    <Modal
      title="Your API secret"
      onClose={onClose}
      width={720}
      footer={
        <>
          <label style={{ marginRight: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span className="hint">I have stored the secret somewhere safe</span>
          </label>
          <button className="btn btn-primary" disabled={!acknowledged} onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <div className="banner banner-error">
        <div>
          <b>This is the only time the secret is shown.</b>
          <div className="hint" style={{ marginTop: 4 }}>
            It is stored as a hash, so nobody — not an administrator, not the database — can
            recover it. If it is lost, generate a new key; the old one stops working immediately.
          </div>
        </div>
      </div>

      <CopyField label="API key" value={issued.key_id} />
      <CopyField label="API secret" value={issued.api_secret} />

      <div className="field">
        <label>Example request</label>
        <pre className="code-block">{example}</pre>
      </div>
    </Modal>
  );
}

function IssueForm({
  hasExistingKey,
  onIssued,
}: {
  hasExistingKey: boolean;
  onIssued: (issued: IssuedApiKey) => void;
}) {
  const { user } = useAuth();
  const { data: catalogue } = usePermissionCatalogue();
  const issue = useIssueApiKey();

  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<string[]>(DEFAULT_PERMISSIONS);
  const [showPermissions, setShowPermissions] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // A key can only ever hold what its owner holds, so offering more would be
  // offering a request the server will refuse.
  const held = new Set(user?.permissions ?? []);
  const available = (catalogue?.data ?? []).filter(
    (p) => held.has(p.key) && !NEVER_ON_A_KEY.has(p.key),
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      onIssued(
        await issue.mutateAsync({
          name: name.trim() || undefined,
          permissions: permissions.length > 0 ? permissions : undefined,
        }),
      );
      setName('');
    } catch (err) {
      setError(err);
    }
  }

  return (
    <form onSubmit={submit}>
      <ErrorBanner error={error} />

      {hasExistingKey && (
        <div className="banner banner-info">
          You already have a key. Generating a new one revokes it immediately — anything still using
          the old key will start failing.
        </div>
      )}

      <div className="field">
        <label>What is this key for?</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Website lead form"
          maxLength={100}
        />
        <span className="hint">A label so you can recognise it later. Not used for anything else.</span>
      </div>

      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setShowPermissions((v) => !v)}
      >
        {showPermissions ? 'Hide permissions' : `Permissions: ${permissions.join(', ') || 'none'}`}
      </button>

      {showPermissions && (
        <div className="card" style={{ margin: '12px 0' }}>
          <div className="card-body">
            <p className="hint" style={{ marginTop: 0 }}>
              Keep this as narrow as the job needs. A key limited to creating leads is useless to
              anyone who steals it; a key with your full access is your account. Deleting, merging
              and anything touching users or roles cannot be put on a key at all.
            </p>
            <div className="perm-grid">
              {available.map((permission) => (
                <label key={permission.key} className="perm-option">
                  <input
                    type="checkbox"
                    checked={permissions.includes(permission.key)}
                    onChange={(e) =>
                      setPermissions((current) =>
                        e.target.checked
                          ? [...current, permission.key]
                          : current.filter((p) => p !== permission.key),
                      )
                    }
                  />
                  <span>
                    <b>{permission.key}</b>
                    <span className="hint">{permission.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <button className="btn btn-primary" disabled={issue.isPending || permissions.length === 0}>
          {issue.isPending ? 'Generating…' : hasExistingKey ? 'Generate a new key' : 'Generate API key'}
        </button>
      </div>
    </form>
  );
}

function KeySummary({ apiKey, onRevoke }: { apiKey: ApiKey; onRevoke: () => void }) {
  const example = `curl -X POST ${window.location.origin}/api/v1/leads \\
  -H "x-api-key: ${apiKey.key_id}" \\
  -H "x-api-secret: <your secret>" \\
  -H "content-type: application/json" \\
  -d '{"full_name":"Ananya Rao","phone":"9812345678","source":"api"}'`;

  return (
    <>
      <div className="kv">
        <span>API key</span>
        <span className="mono">{apiKey.key_id}</span>
      </div>
      <div className="kv">
        <span>API secret</span>
        <span className="mono hint">{apiKey.secret_hint}</span>
      </div>
      <div className="kv">
        <span>Label</span>
        <span>{apiKey.name}</span>
      </div>
      <div className="kv">
        <span>Can do</span>
        <span>
          {apiKey.permissions.map((p) => (
            <span className="tag" key={p}>{p}</span>
          ))}
        </span>
      </div>
      <div className="kv">
        <span>Last used</span>
        <span>{apiKey.last_used_at ? relativeTime(apiKey.last_used_at) : 'never'}</span>
      </div>
      <div className="kv">
        <span>Created</span>
        <span>{formatDateTime(apiKey.created_at)}</span>
      </div>

      <div className="field" style={{ marginTop: 16 }}>
        <label>How to use it</label>
        <pre className="code-block">{example}</pre>
        <span className="hint">
          Send both headers instead of a login token. The lead is created as you, assigned by your
          routing rules, and recorded in the audit log as an API action.
        </span>
      </div>

      <button className="btn btn-ghost btn-danger btn-sm" onClick={onRevoke}>
        Revoke this key
      </button>
    </>
  );
}

export function CredentialsPage() {
  const { can } = useAuth();
  const { data: myKey, isPending } = useMyApiKey();
  const canManage = can('api_key.manage');
  const { data: allKeys = [] } = useApiKeys(canManage);
  const revoke = useRevokeApiKey();

  const [issued, setIssued] = useState<IssuedApiKey | null>(null);
  const [error, setError] = useState<unknown>(null);

  async function revokeKey(key: ApiKey, mine: boolean) {
    const who = mine ? 'your key' : `${key.owner.name ?? 'this user'}’s key`;
    if (!confirm(`Revoke ${who}? Anything using it stops working immediately, and it cannot be restored.`)) return;
    setError(null);
    try {
      await revoke.mutateAsync(key.id);
    } catch (err) {
      setError(err);
    }
  }

  // Other people's keys — mine already has its own card above.
  const othersKeys = allKeys.filter((k) => k.id !== myKey?.id);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>API credentials</h1>
          <p className="sub">
            A key and secret for systems that need to reach this CRM without a login — a website
            form posting leads, an automation, another application
          </p>
        </div>
      </div>

      <ErrorBanner error={error} />

      <div className="card">
        <div className="card-head">
          <div>
            <b>Your API key</b>
            <p className="sub" style={{ margin: 0 }}>
              Acts as you, limited to the permissions you give it
            </p>
          </div>
        </div>
        <div className="card-body">
          {isPending ? (
            <div className="hint">Loading…</div>
          ) : myKey ? (
            <>
              <KeySummary apiKey={myKey} onRevoke={() => void revokeKey(myKey, true)} />
              {canManage && (
                <>
                  <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid var(--rule)' }} />
                  <IssueForm hasExistingKey onIssued={setIssued} />
                </>
              )}
            </>
          ) : canManage ? (
            <IssueForm hasExistingKey={false} onIssued={setIssued} />
          ) : (
            <EmptyState title="No API key">
              You do not have one, and issuing keys needs the “Manage API keys” permission. An
              administrator can grant it from Roles &amp; permissions.
            </EmptyState>
          )}
        </div>
      </div>

      {canManage && (
        <div className="card">
          <div className="card-head">
            <div>
              <b>Keys in this organisation</b>
              <p className="sub" style={{ margin: 0 }}>
                Every live key and who it acts as. Revoking one is immediate.
              </p>
            </div>
          </div>
          <div className="card-body">
            {othersKeys.length === 0 ? (
              <EmptyState title="No other keys">
                Nobody else has issued a key. Each person gets one, and it carries their own access.
              </EmptyState>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Acts as</th>
                      <th>Can do</th>
                      <th>Last used</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {othersKeys.map((key) => (
                      <tr key={key.id}>
                        <td>
                          <b>{key.name}</b>
                          <div className="mono hint">{key.key_id}</div>
                        </td>
                        <td>
                          {key.owner.name ?? '—'}
                          <div className="hint">{key.owner.email}</div>
                        </td>
                        <td>
                          {key.permissions.map((p) => (
                            <span className="tag" key={p}>{p}</span>
                          ))}
                        </td>
                        <td>{key.last_used_at ? relativeTime(key.last_used_at) : 'never'}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="btn btn-ghost btn-sm btn-danger"
                            disabled={revoke.isPending}
                            onClick={() => void revokeKey(key, false)}
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {issued && <SecretModal issued={issued} onClose={() => setIssued(null)} />}
    </div>
  );
}
